import { Typography } from "@mui/material";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

import type { SxProps, Theme } from "@mui/material";

// Shared by the regular progress steps and the terminal step so every step
// renders at the same icon and label size.
export const PROGRESS_STEP_LABEL_SX: SxProps<Theme> = {
  flexDirection: "column",
  alignItems: "center",

  "& .MuiStepLabel-iconContainer": {
    paddingRight: 0,
    marginBottom: 0.5,
  },

  "& svg": {
    fontSize: 25, // default ~24
  },

  "& .MuiStepLabel-label": {
    marginTop: 0.5,
    textAlign: "center",
    whiteSpace: "nowrap",
  },
};

const TERMINAL_STEP_LABEL_BY_STATUS: Partial<
  Record<ShipmentStatusValue, string>
> = {
  CANCELLED: "Cancelled",
  BOOKING_CANCELLED: "Booking Cancelled",
  EXPIRED: "Expired",
};

function isTerminalStatus(
  shipmentStatus: ShipmentStatusValue | undefined,
): boolean {
  return !!shipmentStatus && shipmentStatus in TERMINAL_STEP_LABEL_BY_STATUS;
}

interface VisibleProgressSteps {
  steps: string[];
  activeStep: number;
}

/*
  When a shipment reaches a terminal status (cancelled or expired) the progress
  bar is truncated to the steps the shipment actually reached, so the terminal
  step is always the last step rather than trailing a row of unreached future
  steps.
*/
export function getVisibleProgressSteps(
  allSteps: string[],
  progressValue: number,
  shipmentStatus: ShipmentStatusValue | undefined,
): VisibleProgressSteps {
  if (!isTerminalStatus(shipmentStatus)) {
    return { steps: allSteps, activeStep: progressValue };
  }

  const steps = allSteps.slice(0, progressValue + 1);
  return { steps, activeStep: steps.length };
}

/*
  Returns the trailing error step rendered when a shipment is cancelled or
  expired. Shared by the pre-booking and post-booking progress steppers so both
  surface the terminal state with identical styling.
*/
export function getTerminalProgressStep(
  shipmentStatus: ShipmentStatusValue | undefined,
): JSX.Element | null {
  if (!isTerminalStatus(shipmentStatus)) {
    return null;
  }

  return (
    <Step completed last>
      <StepLabel error sx={PROGRESS_STEP_LABEL_SX}>
        <Typography variant="caption" color="error">
          {TERMINAL_STEP_LABEL_BY_STATUS[shipmentStatus as ShipmentStatusValue]}
        </Typography>
      </StepLabel>
    </Step>
  );
}
